import React from 'react';
import { Link } from 'react-router-dom';
import {
  Droplets,
  HeartHandshake,
  Eye,
  Compass,
  Users,
  Sparkles,
  ArrowRight,
  MapPin,
  CalendarDays,
  HandHeart,
  BookOpen,
  Church,
} from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import { Badge } from '../components/ui/Badge';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

const HERO =
  'https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const IMG = {
  community:
    'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  worship:
    'https://images.unsplash.com/photo-1507692049790-de58290a4334?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  bible:
    'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
};

const VALUES = [
  {
    icon: HeartHandshake,
    title: 'Comunhão',
    desc: 'Vivemos a vida cristã em comunidade, por meio de nossas células e redes.',
    tone: 'bg-rose-50 text-rose-600',
  },
  {
    icon: BookOpen,
    title: 'Discipulado',
    desc: 'Incentivamos o crescimento espiritual e a formação de discípulos de Cristo.',
    tone: 'bg-primary-light text-primary',
  },
  {
    icon: Sparkles,
    title: 'Dons & Serviço',
    desc: 'Cada cristão descobre e exerce os dons que recebeu para edificar a Igreja.',
    tone: 'bg-accent-light text-accent-dark',
  },
  {
    icon: HandHeart,
    title: 'Amor & Graça',
    desc: 'Servimos à comunidade com amor, graça e verdade, refletindo o coração de Deus.',
    tone: 'bg-emerald-100 text-emerald-700',
  },
];

const PILLARS = [
  {
    icon: Church,
    title: 'Anunciar o Evangelho',
    desc: 'Levar a mensagem de Jesus a cada pessoa, para que todos tenham a oportunidade de conhecê-Lo.',
  },
  {
    icon: Users,
    title: 'Formar Discípulos',
    desc: 'Caminhar com cada pessoa rumo à maturidade espiritual, comunhão e serviço.',
  },
  {
    icon: Droplets,
    title: 'Água Viva para Todos',
    desc: 'Que cada um beba da Água Viva e se torne canal para que outros encontrem vida abundante.',
  },
];

function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO} alt="Comunidade Águas Vivas" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/85 via-primary-dark/70 to-primary-dark/90" />
      </div>

      <div className="app-container relative py-16 md:py-24">
        <div className="max-w-2xl">
          <Badge tone="accent" className="mb-4">
            <Droplets size={12} /> Comunidade Águas Vivas · Caratinga
          </Badge>
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white md:text-5xl">
            Quem somos
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
            Uma igreja cristã evangélica que anuncia o Evangelho, forma discípulos e serve à
            comunidade com amor, graça e verdade.
          </p>
          <Link
            to="/celulas"
            data-testid="sobre-saiba-mais"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 font-heading text-sm font-bold text-white shadow-lift transition-transform duration-200 hover:-translate-y-0.5 hover:bg-accent-dark"
          >
            SAIBA MAIS <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="app-container mt-10">
      <div className="grid items-center gap-8 md:grid-cols-2">
        <div className="relative">
          <div className="overflow-hidden rounded-2xl shadow-lift">
            <img
              src={IMG.community}
              alt="Comunidade reunida"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -right-3 hidden rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-lift backdrop-blur-xl md:block">
            <p className="font-heading text-3xl font-extrabold text-primary">2003</p>
            <p className="text-xs font-semibold text-ink-soft">Ano de fundação</p>
          </div>
        </div>

        <div>
          <Badge tone="primary" className="mb-3">
            <Compass size={12} /> Nossa História
          </Badge>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
            Uma família que cresce junta
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-ink-soft md:text-base">
            A Comunidade Águas Vivas é uma igreja cristã evangélica localizada na cidade de
            Caratinga, Minas Gerais.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-soft md:text-base">
            Desde sua fundação, em <strong className="font-semibold text-ink">2003</strong>, tem
            como missão anunciar o Evangelho de Jesus Cristo, formar discípulos e servir à
            comunidade com amor, graça e verdade.
          </p>
          <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-primary">
            <MapPin size={16} /> Caratinga · Minas Gerais
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <section className="mt-14 bg-white py-12">
      <div className="app-container">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div className="order-2 md:order-1">
            <Badge tone="accent" className="mb-3">
              <HeartHandshake size={12} /> Nossa Missão
            </Badge>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
              Anunciar, formar e servir
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-ink-soft md:text-base">
              Anunciamos o Evangelho, promovemos comunhão por meio de nossas células, incentivamos
              o crescimento espiritual através do discipulado e encorajamos cada cristão a
              descobrir e exercer os dons que recebeu para a edificação da Igreja e a expansão do
              Reino de Deus.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft md:text-base">
              Nossa missão continua até que cada pessoa tenha a oportunidade de conhecer a Jesus,
              beber da Água Viva e tornar-se também um instrumento para que outros encontrem a vida
              abundante que há Nele.
            </p>
          </div>

          <div className="order-1 md:order-2">
            <div className="overflow-hidden rounded-2xl shadow-lift">
              <img
                src={IMG.worship}
                alt="Momento de adoração"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl border border-slate-200 bg-canvas p-6 shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-light text-primary">
                <p.icon size={22} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-ink">{p.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisionSection() {
  return (
    <section className="app-container mt-14">
      <div className="grid items-center gap-8 md:grid-cols-2">
        <div className="relative">
          <div className="overflow-hidden rounded-2xl shadow-lift">
            <img
              src={IMG.bible}
              alt="Leitura da Palavra"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -left-3 hidden rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-lift backdrop-blur-xl md:block">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-light text-accent-dark">
              <Eye size={20} />
            </span>
            <p className="mt-2 font-heading text-sm font-bold text-ink">Vidas transformadas</p>
          </div>
        </div>

        <div>
          <Badge tone="primary" className="mb-3">
            <Eye size={12} /> Nossa Visão
          </Badge>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
            Vidas restauradas, famílias fortalecidas
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-ink-soft md:text-base">
            Sonhamos em ver vidas restauradas, famílias fortalecidas e discípulos comprometidos com
            o Reino de Deus, vivendo o Evangelho de forma prática e relevante.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-soft md:text-base">
            Queremos ser uma igreja que cresce não apenas em número, mas principalmente em
            maturidade espiritual, comunhão, serviço e amor ao próximo.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-soft md:text-base">
            Nossa visão é que cada pessoa encontre em Cristo a Água Viva, seja transformada por Sua
            graça e se torne um canal para que muitos outros também experimentem essa mesma vida
            abundante.
          </p>
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  return (
    <section className="mt-14 bg-white py-12">
      <div className="app-container">
        <div className="text-center">
          <Badge tone="accent" className="mb-3">
            <Sparkles size={12} /> Nossos Valores
          </Badge>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
            O que nos move
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-ink-soft md:text-base">
            Princípios que guiam nossa caminhada como comunidade de fé.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="rounded-2xl border border-slate-200 bg-canvas p-6 text-center shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
            >
              <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${v.tone}`}>
                <v.icon size={26} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-ink">{v.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="app-container mt-14">
      <div className="relative overflow-hidden rounded-2xl bg-primary-dark p-6 text-white shadow-lift md:p-10">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
        <div className="absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-accent/20" />
        <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
              <CalendarDays size={24} />
            </span>
            <h3 className="mt-4 font-heading text-xl font-extrabold md:text-2xl">
              Venha fazer parte dessa família
            </h3>
            <p className="mt-2 max-w-md text-sm text-white/80">
              Encontre uma célula perto de você e comece a viver a vida abundante que há em Cristo.
            </p>
          </div>
          <Link
            to="/celulas"
            data-testid="sobre-cta-celulas"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-sm font-bold text-primary transition-transform duration-200 hover:-translate-y-0.5"
          >
            Encontrar uma célula <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Sobre() {
  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <HeroSection />
      <AboutSection />
      <MissionSection />
      <VisionSection />
      <ValuesSection />
      <CtaSection />
    </PageLayout>
  );
}
